import React, { useEffect, useState } from 'react';
import {
  commentActionEnum,
  InspectionTask,
} from 'redux/conductInspectionSlice';
import { useAddCommentMutation } from 'redux/services/inspection';
import { Project } from 'redux/projectSlice';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useUploadAssetMutation } from 'redux/services/project';
import { dataURItoBlob } from 'components/common/FormCaptureInput/utils';
import { userRoles } from 'constants/constants';
import { ChatDrawer } from 'components/ChatComponents/ChatDrawer';

export type ChatConversationType = {
  selectedTask: InspectionTask | null;
  project: Project;
  onDrawerClose: () => void;
  onUpdate: () => void;
};

export const ChatConversation: React.FC<ChatConversationType> = ({
  selectedTask,
  project,
  onDrawerClose,
  onUpdate,
}) => {
  const { projectId, inspectionId } = useParams();
  const [chatInput, setChatInput] = useState('');
  const [commentOption, setCommentOption] = useState('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [imageBase64, setImage] = useState('');
  const [uploadAsset, { isLoading: uploading }] = useUploadAssetMutation();

  const [addComment, { isLoading: submittingComment }] =
    useAddCommentMutation();

  useEffect(() => {
    if (
      (project.role === userRoles.DOER &&
        selectedTask?.currentStatus === commentActionEnum.SendForApproval) ||
      (project.role !== userRoles.DOER &&
        (selectedTask?.currentStatus === commentActionEnum.Disapprove ||
          selectedTask?.currentStatus === commentActionEnum.TaskRaised))
    ) {
      setCommentOption(commentActionEnum.Comment);
    }
  }, [project.role, selectedTask?.currentStatus]);

  const handleDrawerClose = () => {
    setChatInput('');
    setCommentOption('');
    onDrawerClose();
  };
  const handleChatInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setChatInput(e.target.value);
  };

  const handleFileUpload = (file: File) => {
    setUploadedFile(file);
    setImage('');
  };

  const handleImageCapture = (data: string) => {
    setImage(data);
    setUploadedFile(null);
  };

  const handleSendComment = async () => {
    let body: any = {
      commentData: {
        action: commentOption,
        comment: chatInput,
      },
    };

    if (imageBase64 || uploadedFile) {
      const formData = new FormData();
      formData.append(
        'file',
        imageBase64 ? dataURItoBlob(imageBase64) : uploadedFile!,
      );
      formData.append(
        'path',
        `projects/${projectId}/inspections/${inspectionId}/questions/${
          selectedTask!.questionId
        }/tasks/${selectedTask?._id}/${Date.now()}`,
      );
      const uploadRes: any = await uploadAsset(formData);

      if (uploadRes.data) {
        body.commentData.documents = [
          {
            docType: imageBase64
              ? 'image'
              : uploadedFile!.type.includes('image')
              ? 'image'
              : 'pdf',
            url: uploadRes.data.url,
          },
        ];
      } else {
        toast.error(uploadRes.error.data.message || 'Something went wrong');
        return;
      }
    }

    const res: any = await addComment({
      projectId,
      id: inspectionId,
      qId: selectedTask?.questionId,
      body,
    });

    if (res.data) {
      setChatInput('');
      setUploadedFile(null);
      setImage('');
      onUpdate();
    } else {
      toast.error(res.error?.data?.message);
    }
  };

  const handleCommentOptionChange = (val: string) => {
    if (val === commentActionEnum.Approve) {
      setChatInput(commentActionEnum.Approve);
    } else if (commentOption === commentActionEnum.Approve) {
      setChatInput('');
    }
    setCommentOption(val);
  };

  const handleAttachmentHolderClose = () => {
    setImage('');
    setUploadedFile(null);
  };

  return (
    <ChatDrawer
      project={project}
      uploading={uploading}
      taskStatus={selectedTask?.status}
      description={selectedTask?.questionInfo?.question ?? ''}
      submittingComment={submittingComment}
      commentOption={commentOption}
      comments={selectedTask?.questionInfo.comments ?? []}
      chatInput={chatInput}
      currentStatus={selectedTask?.currentStatus ?? null}
      imageBase64={imageBase64}
      uploadedFile={uploadedFile}
      isInspectorDoerSame = {selectedTask?.inspector === selectedTask?.doer}
      onClose={handleDrawerClose}
      onCommentOptionChange={handleCommentOptionChange}
      onChatInputChange={handleChatInputChange}
      onSendComment={handleSendComment}
      onAttachmentHolderClose={handleAttachmentHolderClose}
      onImageCapture={handleImageCapture}
      onFileUpload={handleFileUpload}
    />
  );
};
